import { useCallback, useEffect, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { CardsService } from "../services/cardsService";
import type { CardStatsRange, CardStatsResponse } from "../types/card-stats.types";

export interface UseCardStatsState {
  stats: CardStatsResponse | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useCardStats(
  cardId: string | undefined,
  range: CardStatsRange,
  refreshKey?: number,
): UseCardStatsState {
  const [stats, setStats] = useState<CardStatsResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!cardId) {
      setStats(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await CardsService.fetchCardStats(cardId, range);
      setStats(response.data);
    } catch (err) {
      const message = HttpApiService.getErrorMessage(err) || "Could not load card stats";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [cardId, range]);

  // refreshKey forces an in-place refetch without remounting the consumer.
  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  return { stats, isLoading, error, refresh: load };
}
